
import { usePageTitle } from '../../hooks/usePageTitle';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';

export default function Terms() {
  usePageTitle('page_title_terms');

  const { i18n } = useTranslation();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": i18n.language === 'zh' ? "服务条款 - Koluno" : "Terms of Service - Koluno",
    "description": i18n.language === 'zh' ? 
      "Koluno服务条款详细说明平台使用规则、用户权利义务和服务内容。请仔细阅读并遵守相关条款。" :
      "Koluno terms of service detail platform usage rules, user rights and obligations, and service content. Please read carefully and comply with relevant terms.",
    "url": `${import.meta.env.VITE_SITE_URL || "https://koluno.com"}/terms`,
    "dateModified": "2024-12-01",
    "publisher": {
      "@type": "Organization",
      "name": "Koluno"
    }
  };

  return (
    <div className="min-h-screen">
      <SEOHead
        title={i18n.language === 'zh' ? "服务条款 - Koluno" : "Terms of Service - Koluno"}
        description={i18n.language === 'zh' ? 
          "Koluno服务条款详细说明平台使用规则、用户权利义务和服务内容。请仔细阅读并遵守相关条款。" :
          "Koluno terms of service detail platform usage rules, user rights and obligations, and service content. Please read carefully and comply with relevant terms."
        }
        keywords={i18n.language === 'zh' ? 
          "服务条款,使用协议,用户协议,平台规则,法律条款" :
          "terms of service,user agreement,platform rules,legal terms,service agreement"
        }
        ogTitle={i18n.language === 'zh' ? "服务条款 - Koluno" : "Terms of Service - Koluno"}
        ogDescription={i18n.language === 'zh' ? 
          "Koluno服务条款详细说明平台使用规则、用户权利义务和服务内容。请仔细阅读并遵守相关条款。" :
          "Koluno terms of service detail platform usage rules, user rights and obligations, and service content. Please read carefully and comply with relevant terms."
        }
        ogUrl={`${import.meta.env.VITE_SITE_URL || "https://koluno.com"}/terms`}
        structuredData={structuredData}
      />
      
      <Header />
      
      <main className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {i18n.language === 'zh' ? '服务条款' : 'Terms of Service'}
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-pink-600 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600">
              {i18n.language === 'zh' ? 
                '使用我们的服务前，请仔细阅读以下条款' :
                'Please read the following terms carefully before using our services'
              }
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {i18n.language === 'zh' ? '服务概述' : 'Service Overview'}
              </h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  {i18n.language === 'zh' ? 
                    'Koluno是一个全球达人整合营销平台，致力于连接品牌与创作者，提供以下服务：' :
                    'Koluno is a global influencer integrated marketing platform dedicated to connecting brands with creators, providing the following services:'
                  }
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    {i18n.language === 'zh' ? 
                      '达人招募与筛选服务' :
                      'Creator recruitment and screening services'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '品牌与达人的匹配服务' :
                      'Brand and creator matching services'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '营销活动策划与执行' :
                      'Marketing campaign planning and execution'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '数据分析与效果评估' :
                      'Data analysis and performance evaluation'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '结算与支付服务' :
                      'Settlement and payment services'
                    }
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {i18n.language === 'zh' ? '用户资格' : 'User Eligibility'}
              </h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  {i18n.language === 'zh' ? 
                    '使用我们的服务，您需要满足以下条件：' :
                    'To use our services, you must meet the following conditions:'
                  }
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    {i18n.language === 'zh' ? 
                      '年满18周岁或在您所在司法管辖区的法定年龄' :
                      'Be at least 18 years old or the legal age in your jurisdiction'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '具有完全民事行为能力' :
                      'Have full civil capacity'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '提供真实、准确、完整的个人信息' :
                      'Provide true, accurate, and complete personal information'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '遵守所在国家和地区的相关法律法规' :
                      'Comply with relevant laws and regulations in your country and region'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '拥有合法的社交媒体账号和内容创作权利' :
                      'Own legitimate social media accounts and content creation rights'
                    }
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {i18n.language === 'zh' ? '达人义务' : 'Creator Obligations'}
              </h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  {i18n.language === 'zh' ? 
                    '作为平台达人，您需要承担以下义务：' :
                    'As a platform creator, you need to undertake the following obligations:'
                  }
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    {i18n.language === 'zh' ? 
                      '提供真实的粉丝数据和账号信息' :
                      'Provide authentic follower data and account information'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '按照合作协议要求创作和发布内容' :
                      'Create and publish content according to collaboration agreement requirements'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '确保内容符合平台规范和法律要求' :
                      'Ensure content complies with platform standards and legal requirements'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '及时响应品牌方和平台的沟通' :
                      'Respond promptly to communications from brands and the platform'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '保护品牌方的商业机密和知识产权' :
                      'Protect brands\' business secrets and intellectual property'
                    }
                  </li>
                  <li>
                    {i18n.language === 'zh' ? 
                      '遵守广告标识和披露要求' :
                      'Comply with advertising identification and disclosure requirements'
                    }
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {i18n.language === 'zh' ? '联系我们' : 'Contact Us'}
              </h2>
              <div className="text-gray-700 space-y-4">
                <p>
                  {i18n.language === 'zh' ? 
                    '如果您对本服务条款有任何疑问，请联系我们：' :
                    'If you have any questions about these terms of service, please contact us:'
                  }
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p><strong>{i18n.language === 'zh' ? '邮箱：' : 'Email:'}</strong> business@koluno.com</p>
                  <p><strong>{i18n.language === 'zh' ? '微信：' : 'WeChat:'}</strong> ljy520155</p>
                  <p><strong>Telegram:</strong> @koluno2020</p>
                </div>
              </div>
            </section>

            <div className="border-t border-gray-200 pt-6 mt-8">
              <p className="text-sm text-gray-500 text-center">
                {i18n.language === 'zh' ? 
                  '最后更新时间：2024年12月' :
                  'Last updated: December 2024'
                }
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
